import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Filter } from "lucide-react";
import { cn } from "@/lib/cn";

interface FilterBarContextValue {
  inBar: boolean;
  /** Lets a control tell the bar whether it currently holds a value. */
  report: ((id: string, active: boolean) => void) | null;
  /**
   * True when a {@link FilterScope} above already owns the tally. A `FilterBar`
   * nested inside one then renders as a plain row instead of starting a second,
   * rival tally that would only see its own children.
   */
  scoped: boolean;
  /** Resets every filter in the scope; null when the scope offers no reset. */
  onClear: (() => void) | null;
}

const FilterBarContext = createContext<FilterBarContextValue>({
  inBar: false,
  report: null,
  scoped: false,
  onClear: null,
});

/**
 * How many filters are applied, kept apart from {@link FilterBarContext} on
 * purpose: every control consumes that one, so folding a value that changes on
 * each keystroke into it would re-render the whole bar. Only the label reads
 * this.
 */
const FilterCountContext = createContext(0);

/**
 * Tint applied to a filter control that currently holds a value, so an active
 * filter is visible without reading every field in the bar.
 */
export const FILTER_ACTIVE_CLASS = "bg-bridge-accent/10 border-bridge-accent/60";

/**
 * Whether a filter control should show itself as applied, given whether it holds
 * a value. Returns false outside a {@link FilterBar} or {@link FilterScope}, so
 * the same controls stay untinted in forms and drawers.
 *
 * Registering with the scope is the other half of the job: "is any filter
 * applied" — which decides whether Clear Filters is offered — is then derived
 * from the very same per-control judgement that drives the highlight, rather
 * than each page re-deriving it from its own filter shape and drifting out of
 * step.
 */
export function useFilterHighlight(hasValue: boolean): boolean {
  const { inBar, report } = useContext(FilterBarContext);
  const id = useId();
  const active = inBar && hasValue;

  useEffect(() => {
    if (!report) return;
    report(id, active);
    // On unmount the control is gone, so it no longer constitutes a filter —
    // matters for bars whose controls are conditionally rendered.
    return () => report(id, false);
  }, [report, id, active]);

  return active;
}

/**
 * Opts a subtree back out of filter highlighting. Popups (a select's type-ahead
 * box, a date picker, a column filter's panel) render through portals, which
 * context still flows through — without this their inputs would light up as if
 * they were filters themselves, and would count towards the scope's active
 * tally on top of the control that already registered them.
 */
export function FilterBarBoundary({ children }: { children: ReactNode }) {
  const value = useMemo<FilterBarContextValue>(
    () => ({ inBar: false, report: null, scoped: false, onClear: null }),
    [],
  );
  return <FilterBarContext.Provider value={value}>{children}</FilterBarContext.Provider>;
}

/**
 * Owns the "which filters are applied" tally for one table. Wrap it around
 * everything that can hold a filter — the {@link FilterBar} above the table
 * *and* the table itself — so per-column filters in the header count towards
 * the same badge and the same Clear Filters as the controls in the bar.
 *
 * Without it a `FilterBar` scopes the tally to its own children, which is still
 * the right thing for pages whose filters all live in the bar.
 */
export function FilterScope({
  onClear,
  children,
}: {
  /** Resets every filter in the scope. Must leave the sort order alone. */
  onClear?: () => void;
  children: ReactNode;
}) {
  const [activeIds, setActiveIds] = useState<ReadonlySet<string>>(() => new Set());

  const report = useCallback((id: string, active: boolean) => {
    setActiveIds((prev) => {
      // Bail when nothing changes, otherwise every control's effect would set
      // state on each render and spin.
      if (prev.has(id) === active) return prev;
      const next = new Set(prev);
      if (active) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Pages pass an inline arrow here. Reading it through a ref keeps the context
  // value stable across renders, so a new closure doesn't re-render every
  // control in the scope on each keystroke.
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;
  const canClear = Boolean(onClear);
  const clearAll = useCallback(() => onClearRef.current?.(), []);

  const value = useMemo<FilterBarContextValue>(
    () => ({ inBar: true, report, scoped: true, onClear: canClear ? clearAll : null }),
    [report, canClear, clearAll],
  );

  return (
    <FilterBarContext.Provider value={value}>
      <FilterCountContext.Provider value={activeIds.size}>{children}</FilterCountContext.Provider>
    </FilterBarContext.Provider>
  );
}

/** How many filters the surrounding scope currently has applied. */
export function useAppliedFilterCount(): number {
  return useContext(FilterCountContext);
}

/**
 * The bar's leading "Filters" label, badged with how many are currently applied.
 * A component rather than markup in each page so the count comes from the
 * scope's own tally — the same one behind each control's highlight — instead of
 * every page counting its filter shape by hand.
 */
export function FilterBarLabel({ className }: { className?: string }) {
  const count = useContext(FilterCountContext);
  return (
    <div className={cn("flex items-center gap-2 text-bridge-muted", className)}>
      <Filter size={14} />
      <span className="text-xs uppercase tracking-wide">Filters</span>
      {count > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-bridge-accent text-white text-[10px] font-bold leading-none"
          aria-label={`${count} ${count === 1 ? "filter" : "filters"} applied`}
        >
          {count}
        </span>
      )}
    </div>
  );
}

/**
 * Resets every filter in the surrounding scope. Renders nothing until at least
 * one filter is applied, and nothing at all when the scope offers no reset —
 * so it can be dropped anywhere a page wants the control to appear.
 */
export function ClearFiltersButton({ className }: { className?: string }) {
  const { onClear } = useContext(FilterBarContext);
  const count = useContext(FilterCountContext);
  if (!onClear || count === 0) return null;
  return (
    <button
      type="button"
      onClick={onClear}
      className={cn(
        "whitespace-nowrap text-sm font-medium text-bridge-muted transition-colors hover:text-bridge-fg",
        className,
      )}
    >
      Clear filters
    </button>
  );
}

interface FilterBarProps {
  className?: string;
  children: ReactNode;
  /**
   * Resets every filter. When given, a Clear Filters link appears at the end of
   * the bar as soon as at least one control holds a value. Implementations must
   * leave the sort order alone — clearing changes which rows are listed, not how
   * they are ordered.
   *
   * Ignored when the bar sits inside a {@link FilterScope}: the scope spans the
   * table as well, so it owns the reset for the bar and the column filters
   * alike.
   */
  onClear?: () => void;
}

/**
 * Row of filter controls above a table. Controls inside it (`Input`,
 * `SearchInput`, `Select`, `MenuSelect`, `DiaryDateField`) highlight themselves
 * once they hold a value.
 */
export function FilterBar({ className, children, onClear }: FilterBarProps) {
  const outer = useContext(FilterBarContext);

  const row = (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 px-4 py-3 border-b border-bridge-border/60",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{children}</div>
      <div className="shrink-0">
        <ClearFiltersButton />
      </div>
    </div>
  );

  // A scope above already spans the bar and the table, so reuse its tally
  // rather than starting a second one blind to the column filters.
  if (outer.scoped) return row;

  return <FilterScope onClear={onClear}>{row}</FilterScope>;
}
